import type { FieldChange } from '@/lib/types';
import { generatePatch } from '@/lib/text-versioning';

/** Keys that represent metadata and should never be diffed. */
const METADATA_KEYS = new Set([
  'id', 'createdAt', 'updatedAt', 'eventChanges',
  'ownerId', 'novelId', 'audit', 'pendingFiles',
]);

/**
 * Produces a stable, deterministic JSON string for deep-equality comparison.
 * Sorts object keys to avoid insertion-order false positives.
 */
function stableStringify(value: JSONSerializable, ignoreId = false): string {
  if (value === null || value === undefined) return JSON.stringify(value);
  if (typeof value !== 'object') return JSON.stringify(value);

  if (Array.isArray(value)) {
    return JSON.stringify(value.map(item => JSON.parse(stableStringify(item, ignoreId))));
  }

  const record = value as Record<string, JSONSerializable>;
  const sorted: Record<string, JSONSerializable> = {};
  for (const key of Object.keys(record).sort()) {
    if (ignoreId && key === 'id') continue;
    if (record[key] !== undefined) {
      sorted[key] = typeof record[key] === 'object'
        ? JSON.parse(stableStringify(record[key], ignoreId)) as JSONSerializable
        : record[key];
    }
  }
  return JSON.stringify(sorted);
}

/** Identifies an array element by its most specific key. */
function resolveArrayItemId(item: JSONSerializable, hasIdField: boolean): string {
  if (typeof item === 'string') return item;
  if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
    const record = item as Record<string, JSONSerializable>;
    if (typeof record['characterId'] === 'string') return record['characterId'];
    if (typeof record['organizationId'] === 'string') return record['organizationId'];
    if (typeof record['locationId'] === 'string') return record['locationId'];
    if (hasIdField && typeof record['id'] === 'string') return record['id'];
  }
  return stableStringify(item, !hasIdField);
}

/** Any JSON-serializable value that goes through the delta pipeline. */
type JSONSerializable = string | number | boolean | null | undefined
  | JSONSerializable[]
  | { [key: string]: JSONSerializable };

/**
 * Compares an array field (baseline vs. current) and emits granular
 * add / remove operations instead of a whole-field set.
 */
function diffArray(
  field: string,
  oldArr: JSONSerializable[],
  newArr: JSONSerializable[],
  hasIdField: boolean,
): FieldChange[] {
  const changes: FieldChange[] = [];
  const oldMap = new Map(oldArr.map(item => [resolveArrayItemId(item, hasIdField), item]));
  const newMap = new Map(newArr.map(item => [resolveArrayItemId(item, hasIdField), item]));

  // Additions & updates
  for (const item of newArr) {
    const id = resolveArrayItemId(item, hasIdField);
    const oldItem = oldMap.get(id);
    if (!oldItem) {
      changes.push({ field, operation: 'add', value: item });
    } else if (stableStringify(oldItem, false) !== stableStringify(item, !hasIdField)) {
      changes.push({ field, operation: 'remove', identifier: id });
      changes.push({ field, operation: 'add', value: item });
    }
  }

  // Removals
  for (const item of oldArr) {
    const id = resolveArrayItemId(item, hasIdField);
    if (!newMap.has(id)) {
      changes.push({ field, operation: 'remove', identifier: id });
    }
  }

  return changes;
}

/**
 * Compares a scalar/object field and emits a set or text-patch operation.
 */
function diffScalar(
  field: string,
  oldValue: JSONSerializable,
  newValue: JSONSerializable,
): FieldChange {
  if (field === 'primaryImageId' || field === 'mainImageUrl' || field === 'mainImagePath') {
    return { field, operation: 'set', value: newValue };
  }

  const isTextUpdate = typeof oldValue === 'string' && typeof newValue === 'string';

  if (isTextUpdate && oldValue !== newValue) {
    const patch = generatePatch(oldValue, newValue);
    if (patch) {
      return { field, operation: 'update', value: patch, newValue, backupValue: oldValue };
    }
    return { field, operation: 'set', value: newValue, backupValue: oldValue };
  }

  return {
    field,
    operation: 'set',
    value: newValue,
    backupValue: typeof oldValue === 'string' ? oldValue : undefined,
  };
}

/** Returns true when a value should be treated as "empty / unset". */
function isEmptyish(value: JSONSerializable): boolean {
  if (value === '' || value === null || value === undefined) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

/**
 * Computes an array of `FieldChange` representing the difference
 * between a baseline entity state and the current form state.
 *
 * @param baseline  The entity state *before* the current event.
 * @param current   The entity state *after* the user's edits.
 * @param existingChangeFields  Fields that already had changes in a
 *   prior save — these always get re-evaluated even if both sides look empty.
 */
export function computeFieldDeltas(
  baseline: Record<string, JSONSerializable>,
  current: Record<string, JSONSerializable>,
  existingChangeFields: string[] = [],
): FieldChange[] {
  const existingSet = new Set(existingChangeFields);
  const normalizedCurrent = { ...current };

  // A profile selection is one logical change represented by three persisted
  // fields. UUID identifies the selected gallery item; URL/path are the
  // denormalized values consumed by every display-only surface.
  const images = Array.isArray(normalizedCurrent.images) ? normalizedCurrent.images : [];
  const selectedImageId = typeof normalizedCurrent.primaryImageId === 'string'
    ? normalizedCurrent.primaryImageId
    : undefined;

  const selectedImage = selectedImageId
    ? images.find(image => typeof image === 'object' && image !== null && !Array.isArray(image) && image.id === selectedImageId)
    : undefined;
  if (selectedImage && typeof selectedImage === 'object' && !Array.isArray(selectedImage)) {
    const profile = selectedImage.profile as Record<string, JSONSerializable> | undefined;
    const original = selectedImage.original as Record<string, JSONSerializable> | undefined;
    normalizedCurrent.primaryImageId = selectedImageId;
    normalizedCurrent.mainImageUrl = (profile?.url ?? original?.url ?? '') as JSONSerializable;
    normalizedCurrent.mainImagePath = (profile?.path ?? original?.path ?? '') as JSONSerializable;
  }

  const allKeys = new Set([...Object.keys(baseline), ...Object.keys(normalizedCurrent)]);
  const changes: FieldChange[] = [];

  for (const key of allKeys) {
    if (METADATA_KEYS.has(key)) continue;

    const oldValue = baseline[key] as JSONSerializable;
    const newValue = normalizedCurrent[key] as JSONSerializable;

    // Skip unchanged empty-vs-undefined pairs unless the field was
    // previously changed (user might be intentionally reverting).
    if (!existingSet.has(key)) {
      if (isEmptyish(newValue) && oldValue === undefined) continue;
      if (newValue === undefined && oldValue === undefined) continue;
    }

    const isArray = Array.isArray(newValue) || Array.isArray(oldValue);
    const oldArr = isArray ? (Array.isArray(oldValue) ? oldValue : []) : [];
    const hasIdField = oldArr.some(
      item => typeof item === 'object' && item !== null && !Array.isArray(item) && 'id' in item,
    );

    // Quick deep-equality check before doing detailed diff
    const stableOld = stableStringify(oldValue, false);
    const stableNew = stableStringify(newValue, isArray && !hasIdField);
    if (stableOld === stableNew) continue;

    if (isArray) {
      const newArr = Array.isArray(newValue) ? newValue : [];
      changes.push(...diffArray(key, oldArr, newArr, hasIdField));
    } else {
      changes.push(diffScalar(key, oldValue, newValue));
    }
  }

  return changes;
}

/**
 * Removes `undefined` values from each change to produce clean Firestore-safe
 * objects. For non-remove operations, coerces missing `value` to `null`.
 */
export function scrubChanges(raw: FieldChange[]): FieldChange[] {
  return raw.map(change => {
    const scrubbed: Record<string, string | number | boolean | null | object> = {};
    for (const [key, val] of Object.entries(change)) {
      if (val !== undefined) {
        scrubbed[key] = val as string | number | boolean | null | object;
      } else if (key === 'value' && change.operation !== 'remove') {
        scrubbed[key] = null;
      }
    }
    return scrubbed as FieldChange;
  });
}
